import { useTheme } from '@emotion/react';
import { useAtomValue } from 'jotai';
import React from 'react';

import { blurAtom } from '~src/blur/blur-atom';
import { useLogPosthogEvent } from '~src/commons/services/posthog-service';
import { useTranslations } from '~src/commons/utils/translations/translate';
import { useSessionState } from '~src/global-state';

import { Category, Choice } from '../../api/api';
import { FoldButton } from './fold-button';

export interface FoldingChildProps {
  category: Category;
  hidden: boolean;
}

interface FoldingProviderProps {
  userChoice: Choice;
  children: (props: FoldingChildProps) => React.ReactElement;
}

export const FoldingProvider: React.FC<FoldingProviderProps> = ({ userChoice, children }) => {
  const { tICU } = useTranslations();
  const session = useSessionState();
  const theme = useTheme();
  const logPosthogEvent = useLogPosthogEvent();

  const { shipping_categories_visible_when_folded_count } = session.flags;
  const foldCount = shipping_categories_visible_when_folded_count || 0;
  const enabled = foldCount > 0 && session.categories.length > foldCount;

  const [folded, setFolded] = React.useState(true);
  const blurred = useAtomValue(blurAtom);

  if (!enabled) {
    const elements = session.categories.map(category =>
      children({
        category,
        hidden: false,
      })
    );
    return <>{elements}</>;
  }

  const selectedIdx = session.categories.findIndex(
    category => category.id === userChoice.shipping_category
  );
  const adjustedFoldCount = selectedIdx >= foldCount ? foldCount - 1 : foldCount;

  const elements = session.categories.map((category, idx) =>
    children({
      category,
      // category is hidden instead of unmounted to preserve component state.
      hidden: folded && idx !== selectedIdx && idx >= adjustedFoldCount,
    })
  );

  const handleFoldedChange = () => {
    setFolded(oldFolded => {
      if (oldFolded) {
        logPosthogEvent('Categories Unfolded', {
          categories_shown: session.categories.length - foldCount,
        });
      } else {
        logPosthogEvent('Categories Folded', {
          categories_hidden: session.categories.length - foldCount,
        });
      }
      return !oldFolded;
    });
  };

  return (
    <>
      {elements}
      {enabled && (
        <FoldButton
          folded={folded}
          foldedText={tICU('widget.shipping_categories_folding.BUTTON.SHOW_MORE')}
          unfoldedText={tICU('widget.shipping_categories_folding.BUTTON.SHOW_LESS')}
          ariaSupplementaryText={tICU('widget.aria.SHOW_MORE_DELIVERY_OPTIONS', {
            count: session.categories.length - foldCount,
          })}
          style={{
            borderTop: `1px solid ${theme.neutral91}`,
            paddingTop: '12px',
            paddingBottom: '20px',
          }}
          tabIndex={blurred ? -1 : 0}
          textStyle={{ fontVariationSettings: "'wght' 500" }}
          onClick={handleFoldedChange}
        />
      )}
    </>
  );
};
